import React, { useState, useEffect, use, useMemo } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/EnterpriseDetails.css";
import { formatDateToUserTimeZone } from "../utils/formatDate";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import editIcon from "../assets/icons/edit.png";
import viewIcon from "../assets/icons/viewIcon.png";
import changeStatusIcon from "../assets/icons/changeStatusIcon.png";
import crossIcon from "../assets/icons/crossIcon.png";
import addIcon from "../assets/icons/addIcon.png";
import downloadIcon from "../assets/icons/downloadIcon.png";
import filterIcon from "../assets/icons/filterIcon.png";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useDispatch } from "react-redux";
import { setRefreshSidebar } from "../redux/sidebarSlice";

import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import productIcon from "../assets/icons/ProductIcon.png"; // Assuming you have a product icon

const BASE_URL = `${process.env.REACT_APP_API_URL}/products`;

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    product_name: "",
    product_type: "",
    product_description: "", // ✅ Added,
    product_image: null,
    product_industry: "",
    product_route: "",
    existingImageName: "",
  });

  const [viewData, setViewData] = useState(null);
  const [editingProductId, setEditingProductId] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const MySwal = withReactContent(Swal);
  const dispatch = useDispatch();

  const [productCounts, setProductCounts] = useState({
    totalProducts: 0,
    activeProducts: 0,
    inactiveProducts: 0,
  });

  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");

  const fetchProductCounts = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/status-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProductCounts({
        totalProducts: res.data.total_products,
        activeProducts: res.data.active_products,
        inactiveProducts: res.data.inactive_products,
      });
    } catch (err) {
      console.error("Failed to fetch product counts", err);
    }
  };

  const fetchProducts = async () => {
    if (!token) return showErrorToast(TOAST_MESSAGES.UNAUTHORIZED);

    try {
      const response = await axios.get(BASE_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(response.data);
    } catch (error) {
      console.error("Failed to fetch products", error);
      showErrorToast(TOAST_MESSAGES.PRODUCT.FETCH_ERROR);
    }
  };

  useEffect(() => {
    fetchProductCounts();
    fetchProducts();
  }, []);

  const handleReset = () => {
    setSortOrder("asc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleApply = () => {
    console.log("Sorting by:", sortOrder);
    console.log("Filtering by day:", dayFilter);
  };

  const filteredProducts = products
    .filter((p) => {
      const lower = searchTerm.toLowerCase();
      const name = p.product_name?.toLowerCase() || "";
      const type = p.product_type?.toLowerCase() || "";
      const matchesSearch = name.includes(lower) || type.includes(lower);

      if (!matchesSearch) return false;

      if (dayFilter === "Today" || dayFilter === "Yesterday") {
        const now = new Date();
        const pDate = new Date(p.updated_timestamp);
        const today = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate()
        );
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);

        return dayFilter === "Today"
          ? pDate >= today
          : pDate >= yesterday && pDate < today;
      }

      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.updated_timestamp);
      const dateB = new Date(b.updated_timestamp);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  const totalEntries = filteredProducts.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredProducts.slice(indexOfFirst, indexOfLast);
  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = Math.min(indexOfLast, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const handleChangeStatus = async (product) => {
    const newStatus = product.product_status === "1" ? "0" : "1";
    const newStatusText = newStatus === "1" ? "Active" : "Inactive";

    try {
      const res = await fetch(
        `${BASE_URL}/change-status/${product.product_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ product_status: newStatus }),
        }
      );

      if (!res.ok) throw new Error("Failed to change status");

      await fetchProducts();
      fetchProductCounts();
      dispatch(setRefreshSidebar(true));

      showSuccessToast(
        `${TOAST_MESSAGES.PRODUCT.STATUS_CHANGE_SUCCESS} to ${newStatusText}`
      );
    } catch (err) {
      console.error(err);
      showErrorToast(TOAST_MESSAGES.PRODUCT.STATUS_CHANGE_ERROR);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?"))
      return;

    try {
      const res = await fetch(`${BASE_URL}/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Delete failed");

      await fetchProducts();
      if (currentData.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (product) => {
    setFormData({
      product_name: product.product_name,
      product_type: product.product_type || "",
      product_description: product.product_description || "", // ✅ Added
      product_industry: product.product_industry || "",
      product_route: product.product_route || "",
      product_image: null,
      existingImageName: product.product_image
        ? product.product_image.split("/").pop()
        : "",
    });

    setEditingProductId(product.product_id);
    setShowForm(true);
  };

  const handleView = (product) => {
    setViewData(product);
    setFormData({
      product_name: product.product_name,
      product_type: product.product_type || "",
      product_industry: product.product_industry || "",
      product_route: product.product_route || "",
      product_image: null,
      existingImageName: product.product_image
        ? product.product_image.split("/").pop()
        : "",
    });
    setShowForm(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const formPayload = new FormData();
      formPayload.append("product_name", formData.product_name);
      formPayload.append("product_type", formData.product_type);
      formPayload.append("product_description", formData.product_description); // ✅ Added

      formPayload.append("product_industry", formData.product_industry);
      formPayload.append("product_route", formData.product_route);

      if (formData.product_image) {
        formPayload.append("product_image", formData.product_image);
      }

      const url = editingProductId
        ? `${BASE_URL}/${editingProductId}`
        : BASE_URL;

      const method = editingProductId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}` },
        body: formPayload,
      });

      if (res.status === 401) {
        MySwal.fire({
          icon: "warning",
          title: "Session expired or unauthorized. Please log in again.",
        });
        return;
      }

      if (!res.ok)
        throw new Error(editingProductId ? "Update failed" : "Create failed");

      setFormData({
        product_name: "",
        product_type: "",
        product_description: "", // ✅ Added
        product_industry: "",
        product_route: "",
        product_image: null,
        existingImageName: "",
      });

      setShowForm(false);
      setEditingProductId(null);
      setViewData(null);
      await fetchProducts();
      dispatch(setRefreshSidebar(true));

      showSuccessToast(
        editingProductId
          ? TOAST_MESSAGES.PRODUCT.UPDATE_SUCCESS
          : TOAST_MESSAGES.PRODUCT.CREATE_SUCCESS
      );
    } catch (err) {
      console.error(err);
      showErrorToast(
        editingProductId
          ? TOAST_MESSAGES.PRODUCT.UPDATE_ERROR
          : TOAST_MESSAGES.PRODUCT.CREATE_ERROR
      );
    }
  };

  const closeModal = () => {
    setShowForm(false);
    setFormData({
      product_name: "",
      product_type: "",
      product_industry: "",
      product_route: "",
      product_image: null,
      existingImageName: "",
    });
    setEditingProductId(null);
    setViewData(null);
  };

  const exportExcel = () => {
    const wsData = [
      [
        "Product",
        "Type",
        "Industry",
        "Route",
        "Status",
        "Created",
        "Updated",
        "Created By",
        "Updated By",
      ],
      ...filteredProducts.map((p) => [
        p.product_name,
        p.product_type || "",
        p.product_industry || "",
        p.product_route || "",
        p.product_status === "1" ? "Active" : "Inactive",
        formatDateToUserTimeZone(p.created_timestamp),
        formatDateToUserTimeZone(p.updated_timestamp),
        p.created_by || "",
        p.updated_by || "",
      ]),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Products");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      "product_management.xlsx"
    );
  };

  return (
    <div className="mx-2 pt-1  h-100">
      <div className="d-flex flex-wrap gap-3 mb-2 mt-0 justify-content-evenly align-items-center">
        {[
          {
            label: "Total Products",
            key: "totalProducts",
          },
          {
            label: "Active Products",
            key: "activeProducts",
          },
          {
            label: "Inactive Products",
            key: "inactiveProducts",
          },
        ].map((item, index) => (
          <div key={index} className="dashboard-box p-3 rounded shadow-sm">
            <div className="d-flex align-items-center mb-2">
              <img
                src={productIcon}
                alt={item.label}
                style={{
                  width: "22px",
                  height: "22px",
                  filter: "brightness(1) invert(1)",
                }}
              />
              <h6 className="ms-2 mb-0">{item.label}</h6>
            </div>
            <h2 className="fw-bold">{productCounts[item.key]}</h2>
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2 mt-1 ">
        <span className="fw-bold" style={{ fontSize: "20px" }}>
          Product Management Details
        </span>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          <select
            className="form-select w-auto"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            {[50, 100, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="ms-2 mt-2">Entries per page</label>
        </div>

        <div className="d-flex align-items-center">
          <div className="custom-search-wrapper me-3">
            <div className="input-group custom-search-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search Products"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <button
            className="btn custom btn-outline-secondary me-2 action-button"
            onClick={exportExcel}
            title="Export to Excel"
          >
            <img src={downloadIcon} alt="Download" />
          </button>

          <div className="dropdown">
            <button
              className="btn icon-button filter-dropdown-toggle action-button"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              title="Filter"
            >
              <img src={filterIcon} alt="Filter" />
            </button>

            <div className="dropdown-menu p-2" style={{ minWidth: "200px" }}>
              <h6 className="fw-bolder text-black px-0">Time</h6>
              <hr className="mt-0" />
              {["asc", "desc"].map((order) => (
                <div className="form-check" key={order}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="sortOrder"
                    id={order}
                    value={order}
                    checked={sortOrder === order}
                    onChange={() => setSortOrder(order)}
                  />
                  <label
                    className="form-check-label notification-label"
                    htmlFor={order}
                  >
                    {order === "asc" ? "Ascending" : "Descending"}
                  </label>
                </div>
              ))}

              <hr className="my-2" />
              <h6 className="fw-bolder text-black px-0">By Day</h6>
              <hr className="mt-0" />
              {["Today", "Yesterday", ""].map((day) => (
                <div className="form-check" key={day || "all"}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="dayFilter"
                    id={day || "all"}
                    value={day}
                    checked={dayFilter === day}
                    onChange={() => setDayFilter(day)}
                  />
                  <label
                    className="form-check-label fw-5 notification-label"
                    htmlFor={day || "all"}
                  >
                    {day || "All"}
                  </label>
                </div>
              ))}

              <hr className="mt-1" />
              <div className="d-flex justify-content-between mt-3">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  className="btn dropdown-btn bg-dark text-white btn-sm"
                  onClick={handleApply}
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>

          <button
            className="btn btn-outline-secondary action-button text-dark"
            onClick={() => setShowForm(true)}
            title="Add Product"
          >
            <img src={addIcon} alt="Add" />
          </button>
        </div>
      </div>

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="text-muted fs-5">No entries found</p>
        </div>
      ) : (
        <div className="not-disable">
          <div className="table-responsive">
            <table className="table role-table table-sm w-100">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Industry</th>
                  <th>Route</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((p) => (
                  <tr key={p.product_id}>
                    <td
                    
                    
                    
                    className="d-flex align-items-center gap-2">
                      {p.product_image ? (
                        <img
                          src={`${process.env.REACT_APP_SERVER_URL}/${p.product_image}`}
                          alt="Icon"
                          style={{
                            width: "14px",
                            height: "14px",
                            objectFit: "contain",
                            filter: "brightness(1) invert(1)",
                          }}
                        />
                      ) : null}
                      {p.product_name || "—"}
                    </td>
                    <td>{p.product_description || "—"}</td>
                    <td>{p.product_type || "—"}</td>
                    <td>{p.product_industry || "—"}</td>
                    <td
                    style={{
                        maxWidth: "190px",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >{p.product_route || "—"}</td>
                    <td>{p.product_status === "1" ? "Active" : "Inactive"}</td>
                    <td>{formatDateToUserTimeZone(p.updated_timestamp)}</td>
                    <td>
                      <button
                        className="icon-button"
                        onClick={() => handleView(p)}
                        title="View"
                      >
                        <img src={viewIcon} alt="View" />
                      </button>

                      <button
                        className="icon-button"
                        onClick={() => handleEdit(p)}
                        title="Edit"
                      >
                        <img src={editIcon} alt="Edit" />
                      </button>

                      <button
                        className="icon-button"
                        onClick={() => handleChangeStatus(p)}
                        title="Change Status"
                      >
                        <img src={changeStatusIcon} alt="Change Status" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="fixed-bottom-footer d-flex justify-content-between align-items-center mt-2 table-footer">
            <div className="text-muted">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <nav>
              <ul className="pagination mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(1)}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>

                {pageNumbers.map((num) => (
                  <li
                    key={num}
                    className={`page-item ${
                      num === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageClick(num)}
                    >
                      {num}
                    </button>
                  </li>
                ))}

                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(totalPages)}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}

      {(showForm || viewData) && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content w-50 mx-auto">
              <form onSubmit={handleFormSubmit}>
                <div className="modal-header bg-white justify-content-center position-relative">
                  <h5 className="modal-title m-0">
                    {viewData
                      ? "View Product"
                      : editingProductId
                      ? "Edit Product"
                      : "Add Product"}
                  </h5>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={closeModal}
                    aria-label="Close"
                  >
                    <img src={crossIcon} alt="Close" />
                  </button>
                </div>
                <div className="modal-body">
                  <div className="mb-3 d-flex align-items-end justify-content-center">
                    <div className="me-3">
                      {/* <label className="form-label ms-2">Image</label> */}
                      <div className="d-flex align-items-center  ">
                        {formData.existingImageName && (
                          <img
                            src={`${process.env.REACT_APP_SERVER_URL}/${formData.existingImageName}`}
                            alt="Preview"
                            style={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "8px",
                              objectFit: "cover",
                              border: "1px solid #ccc",
                              marginRight: "8px",
                              filter: "brightness(1) invert(1)",
                            }}
                          />
                        )}
                        <input
                          type="file"
                          className="file-input-reverse"
                          accept="image/*"
                          disabled={!!viewData}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              product_image: e.target.files[0],
                            })
                          }
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-dark h-50 fw-bold"
                      disabled={!!viewData}
                      onClick={() => {
                        // Optional: trigger formData.product_image upload logic
                      }}
                    >
                      Upload
                    </button>
                  </div>

                  {/* <div className="d-flex mb-3 gap-3">
                    <div className="flex-fill">
                      <label className="form-label">Product Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.product_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            product_name: e.target.value,
                          })
                        }
                        required
                        disabled={!!viewData}
                      />
                    </div>

                    <div className="flex-fill">
                      <label className="form-label">Product Type</label>
                      <select
                        className="form-control form-select"
                        value={formData.product_type}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            product_type: e.target.value,
                          })
                        }
                        disabled={!!viewData}
                        required
                      >
                        <option value="">Select Product Type</option>
                        <option value="Machine Learning">
                          Machine Learning
                        </option>
                        <option value="Deep Learning">Deep Learning</option>
                        <option value="Gen AI">Gen AI</option>
                        <option value="Agentic AI">Agentic AI</option>
                        <option value="LLM">LLM</option>
                      </select>
                    </div>
                  </div> */}
                  <div className="row mb-3">
                    <div className="col">
                      <label className="form-label">Product Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.product_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            product_name: e.target.value,
                          })
                        }
                        required
                        disabled={!!viewData}
                      />
                    </div>

                    <div className="col">
                      <label className="form-label">Product Type</label>
                      <select
                        className="form-control form-select"
                        value={formData.product_type}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            product_type: e.target.value,
                          })
                        }
                        disabled={!!viewData}
                        required
                      >
                        <option value="">Select Product Type</option>
                        <option value="Machine Learning">
                          Machine Learning
                        </option>
                        <option value="Deep Learning">Deep Learning</option>
                        <option value="Gen AI">Gen AI</option>
                        <option value="Agentic AI">Agentic AI</option>
                        <option value="LLM">LLM</option>
                      </select>
                    </div>
                  </div>


                  {/* <div className="d-flex mb-3 gap-3">

                    <div className="flex-fill">
                      <label className="form-label">Industry</label>
                      <select
                        className="form-control form-select"
                        value={formData.product_industry}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            product_industry: e.target.value,
                          })
                        }
                        disabled={!!viewData}
                        required
                      >
                        <option value="">Select Industry</option>
                        <option value="Finance">Finance</option>
                        <option value="Transport">Transport</option>
                        <option value="Meteorology">Meteorology</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Real Estate">Real Estate</option>
                        <option value="Security">Security</option>
                        <option value="Law">Law</option>
                        <option value="Infrastructure">Infrastructure</option>
                      </select>
                    </div>

                    <div className="flex-fill">
                      <label className="form-label">Route</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.product_route}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            product_route: e.target.value,
                          })
                        }
                        disabled={!!viewData}
                      />
                    </div>
                  </div> */}
                  <div className="row mb-3">
                    <div className="col">
                      <label className="form-label">Industry</label>
                      <select
                        className="form-control form-select"
                        value={formData.product_industry}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            product_industry: e.target.value,
                          })
                        }
                        disabled={!!viewData}
                        required
                      >
                        <option value="">Select Industry</option>
                        <option value="Finance">Finance</option>
                        <option value="Transport">Transport</option>
                        <option value="Meteorology">Meteorology</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Real Estate">Real Estate</option>
                        <option value="Security">Security</option>
                        <option value="Law">Law</option>
                        <option value="Infrastructure">Infrastructure</option>
                        <option value="Manufacturing">Manufacturing</option>
                        <option value="Agriculture">Agriculture</option>
                      </select>
                    </div>

                    <div className="col">
                      <label className="form-label">Route</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.product_route}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            product_route: e.target.value,
                          })
                        }
                        disabled={!!viewData}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Product Description</label>
                    <textarea
                      className="form-control"
                      value={formData.product_description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          product_description: e.target.value,
                        })
                      }
                      rows={3}
                      disabled={!!viewData}
                    />
                  </div>
                </div>

                <div className="modal-footer bg-white">
                  {viewData ? (
                    <button
                      type="button"
                      className="px-5 rounded text-white bg-dark border-none py-2 fw-bold"
                      onClick={closeModal}
                    >
                      Close
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="px-5 rounded text-white bg-dark border-none py-2 fw-bold"
                    >
                      {editingProductId ? "Update Product" : "Add Product"}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;
